import React, { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

const MOBILE_QUERY = '(max-width:599.95px)';

function rowMissing(missingRows, row) {
  if (!missingRows) return false;
  if (typeof missingRows.has === 'function') return missingRows.has(row);
  return Array.isArray(missingRows) && missingRows.includes(row);
}

function columnHeaderWords(label) {
  const text = label == null ? '' : String(label).trim();
  if (!text) return [''];
  return text.split(/\s+/);
}

export default function MatrixQuestion({
  rows = [],
  columns = [],
  value,
  onChange,
  missingRows,
  disabled = false,
  readOnly = false
}) {
  const mobile = useMediaQuery(MOBILE_QUERY, { noSsr: true });
  const answers = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const rowKey = rows.join('\u0001');
  const [activeIndex, setActiveIndex] = useState(0);
  const [optionsOpen, setOptionsOpen] = useState(true);
  const seenMissing = useRef('');

  useEffect(() => {
    const firstOpen = rows.findIndex((row) => !answers[row]);
    const nextIndex = firstOpen === -1 ? Math.max(0, rows.length - 1) : firstOpen;
    setActiveIndex(nextIndex);
    setOptionsOpen(firstOpen !== -1);
    // Открываем матрицу заново только когда сменился сам вопрос, не при каждом ответе.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowKey]);

  const missingKey = rows.filter((row) => rowMissing(missingRows, row)).join('\n');
  useEffect(() => {
    if (missingKey && missingKey !== seenMissing.current) {
      const idx = rows.findIndex((row) => rowMissing(missingRows, row));
      if (idx >= 0) {
        setActiveIndex(idx);
        setOptionsOpen(true);
      }
    }
    seenMissing.current = missingKey;
  }, [missingKey, rows, missingRows]);

  const showRow = (index, openOptions) => {
    setActiveIndex(index);
    setOptionsOpen(openOptions);
  };

  const choose = (row, col) => {
    if (disabled || readOnly || typeof onChange !== 'function') return;
    onChange({ ...answers, [row]: col });
    const idx = rows.indexOf(row);
    if (idx >= 0 && idx < rows.length - 1) showRow(idx + 1, true);
    else setOptionsOpen(false);
  };

  if (mobile) {
    if (!rows.length) return null;
    const index = Math.min(activeIndex, rows.length - 1);
    const row = rows[index];
    const missing = rowMissing(missingRows, row);
    const answered = !readOnly && answers[row];
    const collapsed = !readOnly && !optionsOpen && Boolean(answered);
    return (
      <Box data-matrix-layout="step" data-matrix-row={row}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Button
            size="small"
            disabled={!index}
            onClick={() => showRow(index - 1, true)}
            sx={{ visibility: index ? 'visible' : 'hidden', px: 0 }}
          >
            Предыдущий
          </Button>
          <Typography variant="caption">{index + 1} из {rows.length}</Typography>
          <Button
            size="small"
            disabled={!readOnly && !answered}
            onClick={() => showRow(index + 1, true)}
            sx={{ visibility: index < rows.length - 1 && (readOnly || answered) ? 'visible' : 'hidden', px: 0 }}
          >
            Следующий
          </Button>
        </Box>
        <Typography
          variant="subtitle2"
          sx={{ mb: 0.5, color: missing ? '#d63031' : 'inherit', fontWeight: 700 }}
        >
          {row}
        </Typography>
        {readOnly ? (
          columns.map((col, colIdx) => (
            <Typography key={`${colIdx}-${col}`} variant="body2" sx={{ py: 0.25 }}>
              {col}
            </Typography>
          ))
        ) : collapsed ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2">{answers[row]}</Typography>
            <Button size="small" onClick={() => setOptionsOpen(true)} sx={{ px: 0, flexShrink: 0 }}>
              Изменить
            </Button>
          </Box>
        ) : (
          <RadioGroup value={answers[row] || ''} onChange={(event) => choose(row, event.target.value)}>
            {columns.map((col, colIdx) => (
              <FormControlLabel
                key={`${colIdx}-${col}`}
                value={col}
                disabled={disabled}
                control={<Radio size="small" />}
                label={col}
                onClick={() => {
                  if (answers[row] === col) choose(row, col);
                }}
                sx={{ alignItems: 'flex-start', mx: 0, py: 0.25 }}
              />
            ))}
          </RadioGroup>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }} data-matrix-layout="table">
      {!readOnly && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Выберите значение в каждой строке
        </Typography>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              {columns.map((col, idx) => (
                <TableCell
                  key={idx}
                  align="center"
                  sx={{ verticalAlign: 'bottom', px: 0.75, width: '1%' }}
                >
                  <Typography variant="caption" component="div" sx={{ lineHeight: 1.2 }}>
                    {columnHeaderWords(col).map((word, wordIdx) => (
                      <Box
                        key={`${idx}-${wordIdx}`}
                        component="span"
                        sx={{ display: 'block', whiteSpace: 'nowrap' }}
                      >
                        {word}
                      </Box>
                    ))}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIdx) => {
              const missing = rowMissing(missingRows, row);
              return (
                <TableRow key={`${rowIdx}-${row}`}>
                  <TableCell sx={{ fontWeight: 500, minWidth: 140 }}>
                    <Typography
                      variant="body2"
                      sx={missing ? { color: '#d63031', fontWeight: 700 } : undefined}
                    >
                      {row}
                    </Typography>
                  </TableCell>
                  {columns.map((col, colIdx) => (
                    <TableCell key={colIdx} align="center">
                      {readOnly ? '—' : (
                        <Radio
                          size="small"
                          disabled={disabled}
                          checked={answers[row] === col}
                          onChange={() => choose(row, col)}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
